import moment from "moment";
import JwtAxios, { BASE_URL } from "apiServices";
import {
  DEFECT_CATEGORY_LISTS,
  FINAL_INSPECTION_ADD_ENDPOINT,
  FINAL_INSPECTION_DELETE_ENDPOINT,
  FINAL_INSPECTION_EDIT_ENDPOINT,
  FINAL_INSPECTION_EXPORT_ENDPOINT,
  FINAL_INSPECTION_LEAD_BYS_ENDPOINT,
  FINAL_INSPECTION_LIST_ENDPOINT,
  FINAL_INSPECTION_ORDER_QTY_ENDPOINT,
  FINAL_INSPECTION_SHOW_ENDPOINT,
  FINAL_INSPECTION_STYLES_ENDPOINT,
  FINAL_INSPECTION_TYPES_ENDPOINT,
} from "apiServices/API_ENDPOINTS";

const DEFECT_CATEGORY_PAGE_SIZE = 100;

export const isRequestCancelled = (error) => error?.name === "CanceledError" || error?.code === "ERR_CANCELED";

const toMessage = (message, fallback) => {
  if (Array.isArray(message)) return message.filter(Boolean).join(", ") || fallback;
  return typeof message === "string" && message ? message : fallback;
};

/** The backend answers some failures with HTTP 200 and `code: 422`, so the envelope code is checked too. */
const unwrap = (response, fallback) => {
  const body = response?.data;
  if (body?.code && body.code !== 200 && body.code !== 201) {
    throw new Error(toMessage(body.message, fallback));
  }
  return body?.data;
};

const get = (path, params, signal) => JwtAxios.get(`${BASE_URL}${path}`, { params, signal });

const readPaginator = (paginator) => {
  const p = paginator || {};
  return {
    rows: Array.isArray(p.data) ? p.data : [],
    current: Number(p.current_page) || 1,
    lastPage: Number(p.last_page) || 1,
    total: Number(p.total) || 0,
  };
};

export const fetchFinalInspectionList = async ({ page, perPage, sort, direction, filters = {} }, signal) => {
  const params = { page, per_page: perPage, sort, direction };
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") params[key] = value;
  });

  const response = await get(FINAL_INSPECTION_LIST_ENDPOINT, params, signal);
  const paginated = readPaginator(unwrap(response, "Failed to load final inspections."));
  return { rows: paginated.rows, total: paginated.total };
};

export const fetchFinalInspectionById = async (id, signal) => {
  const response = await get(`${FINAL_INSPECTION_SHOW_ENDPOINT}/${id}`, undefined, signal);
  return unwrap(response, "Failed to load final inspection.");
};

export const fetchFinalInspectionStyles = async ({ search, page = 1, perPage = 20 }, signal) => {
  const params = { page, per_page: perPage };
  if (search) params.style_no = search;

  const response = await get(FINAL_INSPECTION_STYLES_ENDPOINT, params, signal);
  const paginated = readPaginator(unwrap(response, "Failed to load styles."));
  return { items: paginated.rows, currentPage: paginated.current, lastPage: paginated.lastPage };
};

/** Sum of sales order quantity for the selected style / PO / sub style / color. */
export const fetchFinalInspectionOrderQty = async ({ styleId, poMapId, poMapIds, subStyleId, colorId }, signal) => {
  const params = { style_id: styleId };
  if (Array.isArray(poMapIds) && poMapIds.length > 0) {
    params.po_map_ids = poMapIds;
  } else if (poMapId) {
    params.po_map_id = poMapId;
  }
  if (subStyleId) params.sub_style_id = subStyleId;
  if (colorId) params.color_id = colorId;

  const response = await get(FINAL_INSPECTION_ORDER_QTY_ENDPOINT, params, signal);
  return Number(unwrap(response, "Failed to load order quantity.")) || 0;
};

const fetchActiveList = async (path, fallback, signal) => {
  const response = await get(path, undefined, signal);
  const data = unwrap(response, fallback);
  return (Array.isArray(data) ? data : []).filter((item) => !item.status || item.status === "active");
};

export const fetchInspectionTypes = (signal) =>
  fetchActiveList(FINAL_INSPECTION_TYPES_ENDPOINT, "Failed to load inspection types.", signal);

export const fetchInspectionLeadBys = (signal) =>
  fetchActiveList(FINAL_INSPECTION_LEAD_BYS_ENDPOINT, "Failed to load inspection lead by list.", signal);

export const fetchDefectCategories = async (signal) => {
  const all = [];
  let page = 1;
  let lastPage = 1;
  do {
    const response = await get(DEFECT_CATEGORY_LISTS, { page, per_page: DEFECT_CATEGORY_PAGE_SIZE }, signal);
    const paginated = readPaginator(unwrap(response, "Failed to load defect categories."));
    all.push(...paginated.rows);
    lastPage = paginated.lastPage;
    page += 1;
  } while (page <= lastPage);

  return all.map((category) => ({ id: Number(category.id), name: category.name_en || category.name_bn || `#${category.id}` }));
};

const toSaveResult = (response, fallback) => {
  const body = response?.data;
  if (body?.code === 200 || body?.code === 201) {
    return { ok: true, message: toMessage(body.message, "Saved successfully."), fieldErrors: {} };
  }
  return {
    ok: false,
    message: toMessage(body?.message, fallback),
    fieldErrors: body?.errors && typeof body.errors === "object" ? body.errors : {},
  };
};

const toSaveFailure = (error, fallback) => {
  const body = error?.response?.data;
  return {
    ok: false,
    message: toMessage(body?.message, error?.message || fallback),
    fieldErrors: body?.errors && typeof body.errors === "object" ? body.errors : {},
  };
};

export const createFinalInspection = async (payload) => {
  const fallback = "Failed to create final inspection.";
  try {
    const response = await JwtAxios.post(`${BASE_URL}${FINAL_INSPECTION_ADD_ENDPOINT}`, payload);
    return toSaveResult(response, fallback);
  } catch (error) {
    return toSaveFailure(error, fallback);
  }
};

export const updateFinalInspection = async (id, payload) => {
  const fallback = "Failed to update final inspection.";
  try {
    const response = await JwtAxios.put(`${BASE_URL}${FINAL_INSPECTION_EDIT_ENDPOINT}/${id}`, payload);
    return toSaveResult(response, fallback);
  } catch (error) {
    return toSaveFailure(error, fallback);
  }
};

export const deleteFinalInspection = async (id) => {
  const fallback = "Failed to delete final inspection.";
  try {
    const response = await JwtAxios.delete(`${BASE_URL}${FINAL_INSPECTION_DELETE_ENDPOINT}/${id}`);
    return toSaveResult(response, fallback);
  } catch (error) {
    return toSaveFailure(error, fallback);
  }
};

export const exportFinalInspections = async (filters = {}, signal) => {
  const params = {};
  Object.keys(filters).forEach((key) => {
    if (filters[key] !== undefined && filters[key] !== null && filters[key] !== "") {
      params[key] = filters[key];
    }
  });

  try {
    const response = await JwtAxios.get(`${BASE_URL}${FINAL_INSPECTION_EXPORT_ENDPOINT}`, {
      params,
      responseType: "blob",
      signal,
    });

    if (response.data?.type === "application/json") {
      const text = await response.data.text();
      const json = JSON.parse(text);
      return { ok: false, message: toMessage(json?.message, "Failed to export final inspections.") };
    }

    const disposition = response.headers?.["content-disposition"] || "";
    let fileName = `Final_Product_Inspection_Report_${moment().format("YYYY-MM-DD")}.xlsx`;
    if (disposition && disposition.includes("filename=")) {
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        fileName = match[1].replace(/['"]/g, "").trim();
      }
    }

    const blob = new Blob([response.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { ok: true, message: "Final inspection report downloaded successfully." };
  } catch (error) {
    if (isRequestCancelled(error)) return { ok: false, message: "Export cancelled." };
    return {
      ok: false,
      message: error?.response?.data?.message || error?.message || "Failed to export final inspections.",
    };
  }
};
